import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SelectedRecords {
  education: number[];
  employment: number[];
  licenses: number[];
}

/** Proof-of-submission, kept after the case is created so the confirm step
 * survives reloads and shows the real case IDs. */
export interface VerificationReceipt {
  caseId: number | null;
  verificationIds: number[];
  pendingIds: number[];
  signatureDataUrl: string | null;
  /** How many records were submitted, snapshotted so the confirm screen can
   * show the count after the flow inputs are cleared. */
  recordCount: number;
  createdAt: string;
}

interface VerificationFlowState {
  pricingAccepted: boolean;
  otpVerified: boolean;
  selected: SelectedRecords;
  signatureDataUrl: string | null;
  caseId: number | null;
  /** Case IDs the backend created for this submission. */
  verificationIds: number[];
  /** Case IDs already pending from a prior submission (409 conflicts). */
  pendingIds: number[];
  receipt: VerificationReceipt | null;

  setPricingAccepted: (v: boolean) => void;
  setOtpVerified: (v: boolean) => void;
  toggleRecord: (kind: keyof SelectedRecords, id: number) => void;
  setSignature: (dataUrl: string | null) => void;
  setCaseId: (id: number | null) => void;
  setReceipt: (receipt: VerificationReceipt | null) => void;
  clearReceipt: () => void;
  /** Clear the transient flow inputs (signature, selected records, OTP/pricing
   * progress) after a successful submission while keeping the receipt so the
   * confirm screen survives reloads. Ensures the next verification starts fresh
   * with a blank signature pad instead of the previous one. */
  finishSubmission: () => void;
  reset: () => void;
}

const initial = {
  pricingAccepted: false,
  otpVerified: false,
  selected: { education: [], employment: [], licenses: [] },
  signatureDataUrl: null as string | null,
  caseId: null as number | null,
  verificationIds: [] as number[],
  pendingIds: [] as number[],
  receipt: null as VerificationReceipt | null,
};

export const useCandidateVerificationStore = create<VerificationFlowState>()(
  persist(
    (set) => ({
      ...initial,
      setPricingAccepted: (v) => set({ pricingAccepted: v }),
      setOtpVerified: (v) => set({ otpVerified: v }),
      toggleRecord: (kind, id) =>
        set((s) => {
          const list = s.selected[kind];
          const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
          return { selected: { ...s.selected, [kind]: next } };
        }),
      setSignature: (dataUrl) => set({ signatureDataUrl: dataUrl }),
      setCaseId: (id) => set({ caseId: id }),
      setReceipt: (receipt) => set({ receipt }),
      clearReceipt: () => set({ receipt: null }),
      finishSubmission: () =>
        set({
          pricingAccepted: false,
          otpVerified: false,
          selected: { education: [], employment: [], licenses: [] },
          signatureDataUrl: null,
          caseId: null,
          verificationIds: [],
          pendingIds: [],
          // receipt is intentionally preserved.
        }),
      reset: () =>
        set({
          ...initial,
          selected: { education: [], employment: [], licenses: [] },
          verificationIds: [],
          pendingIds: [],
        }),
    }),
    {
      name: 'tally:candidate-verification',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
